const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api/v1';

export interface CustomerWaitlistPayload {
  email: string;
  name?: string;
  phone?: string;
  city?: string;
}

export interface VendorWaitlistPayload {
  email: string;
  storeName: string;
  phone?: string;
  city?: string;
  productCategories?: string[];
}

export interface WaitlistApiResponse {
  success: boolean;
  message: string;
  data?: any;
}

function parseBackendErrorMessage(json: any, fallbackMessage: string): string {
  if (!json) return fallbackMessage;

  const fieldErrors = json.errors?.fieldErrors || json.data?.fieldErrors;

  if (fieldErrors && typeof fieldErrors === 'object') {
    const messages: string[] = [];

    for (const [field, errs] of Object.entries(fieldErrors)) {
      if (Array.isArray(errs) && errs.length > 0) {
        const rawMsg = errs[0] as string;
        let humanMsg = rawMsg;

        if (rawMsg.includes('>=10 characters') || rawMsg.includes('Too small')) {
          humanMsg = 'Please enter a valid 10-digit phone number (e.g. 09012345678)';
        } else if (rawMsg.toLowerCase().includes('required') || rawMsg.includes('invalid_type')) {
          humanMsg = 'is required';
        } else if (rawMsg.toLowerCase().includes('email')) {
          humanMsg = 'Please enter a valid email address';
        }

        const fieldTitle =
          field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1');

        if (humanMsg.startsWith('Please')) {
          messages.push(humanMsg);
        } else {
          messages.push(`${fieldTitle}: ${humanMsg}`);
        }
      }
    }

    if (messages.length > 0) {
      return messages.join(' | ');
    }
  }

  if (json.message && json.message !== 'Validation failed') {
    return json.message;
  }

  return fallbackMessage;
}

export interface RiderWaitlistPayload {
  email: string;
  name: string;
  phone?: string;
  city?: string;
}

export class WaitlistService {
  static async submitCustomerWaitlist(payload: CustomerWaitlistPayload): Promise<WaitlistApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/waitlist/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const json = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: parseBackendErrorMessage(
            json,
            'Failed to join waitlist. Please check your details and try again.'
          ),
        };
      }

      return {
        success: true,
        message: json.message || "You're on the list! We'll notify you when we launch.",
        data: json.data,
      };
    } catch (error: any) {
      console.error('Customer waitlist error:', error);
      return {
        success: false,
        message: 'Network error. Please check your internet connection and try again.',
      };
    }
  }

  static async submitVendorWaitlist(payload: VendorWaitlistPayload): Promise<WaitlistApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/waitlist/vendors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const json = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: parseBackendErrorMessage(
            json,
            'Failed to submit merchant application. Please check your details and try again.'
          ),
        };
      }

      return {
        success: true,
        message: json.message || 'Application received! Our onboarding team will contact you soon.',
        data: json.data,
      };
    } catch (error: any) {
      console.error('Vendor waitlist error:', error);
      return {
        success: false,
        message: 'Network error. Please check your internet connection and try again.',
      };
    }
  }

  static async submitRiderWaitlist(payload: RiderWaitlistPayload): Promise<WaitlistApiResponse> {
    return this.submitVendorWaitlist({
      storeName: `[Rider] ${payload.name}`,
      email: payload.email,
      phone: payload.phone,
      city: payload.city,
    });
  }
}
